import * as React from 'react';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { IReportSale } from 'utils/interfaces';
import FormattedMass from 'components/FormattedMass';
import FormattedCost from 'components/FormattedCost';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import HeaderCell from 'components/HeaderCell';
import { CSSTransitionGroup } from 'react-transition-group';
import moment from 'moment';
import { connect } from 'react-redux';
import { Sale, SalesActions } from 'redux/ducks/sales';
import { Registration } from 'redux/ducks/data/registrations';
import { sum } from 'utils/array';
import { RootState } from 'redux/rootReducer';
import { getYearlyRegistrationsByDate, RegistrationsActions } from 'redux/ducks/registrations';
import { ThunkDispatch } from 'redux-thunk';
import * as registrationsDispatch from 'redux/ducks/registrations';
import * as salesDispatch from 'redux/ducks/sales';

interface StateProps {
  sales: Sale[];
  registrationsByDate: { [date: string]: Registration[] };
  currency: string;
}

interface DispatchProps {
  getYearlySalesAndRegistrations: () => Promise<void>;
}

export interface OwnProps {
  historyItemOnClick?: (sale: Sale) => void;
  selectedSale?: Partial<Sale>;
  massUnit: string;
}

export interface IComponentState {
  selectedSale?: {
    income: number | string;
    portionPrice: number | string;
    guests: number | string;
    productionCost: number | string;
    productionWeight: number | string;
    portions: number | string;
    date: any;
  };
  error?: boolean;
  orderBy?: keyof Sale;
  sortedSales: Sale[];
  loaded?: boolean;
  order?: 'asc' | 'desc';
  showDataFields?: boolean;
  showSalesHistory?: boolean;
}

type SalesHistoryProps = StateProps & DispatchProps & WrappedComponentProps & OwnProps;

export class SalesHistory extends React.Component<SalesHistoryProps, IComponentState> {
  sales: IReportSale[] = [];

  constructor(props: SalesHistoryProps) {
    super(props);

    this.state = {
      sortedSales: []
    };
  }

  componentDidMount() {
    void this.loadYearlySales();
  }

  // React discards a lifecycle's return value, so the promise is kept out of it
  private async loadYearlySales() {
    await this.props.getYearlySalesAndRegistrations();
    this.sortSales(this.props.sales, this.state.orderBy, this.state.order);
  }

  UNSAFE_componentWillReceiveProps(nextProps: SalesHistoryProps, nextState: IComponentState) {
    if (this.props.sales != nextProps.sales) {
      this.sortSales(nextProps.sales, nextState.orderBy, nextState.order);
    }
  }

  changeSorting = (property: keyof Sale) => {
    const { order } = this.state;

    this.sortSales(this.props.sales, property, order == 'asc' ? 'desc' : 'asc');
  };

  sortSales = (sales: Sale[], orderBy: keyof Sale = 'date', order: 'asc' | 'desc' = 'desc') => {
    const sortByDate = (a: Sale, b: Sale): number => (a[orderBy] < b[orderBy] ? -1 : 1);
    const sortByOther = (a: Sale, b: Sale): number =>
      parseInt(a[orderBy] as string) < parseInt(b[orderBy] as string) ? -1 : 1;

    const sortBy = orderBy === 'date' ? sortByDate : sortByOther;
    const sortFn = (a: Sale, b: Sale): number =>
      order === 'desc' ? -1 * sortBy(a, b) : sortBy(a, b);

    this.setState({
      sortedSales: sales.sort(sortFn),
      orderBy: orderBy,
      order: order
    });
  };

  render() {
    const { order, orderBy, sortedSales } = this.state;
    const { massUnit, selectedSale, currency, registrationsByDate, historyItemOnClick } =
      this.props;
    const headerData = [
      { property: 'date', translationKey: 'date' },
      {
        property: 'income',
        translationKey: 'sales.dialog.table.income',
        values: { currency },
        alignRight: true
      },
      {
        property: 'portionPrice',
        translationKey: 'sales.dialog.table.salesPrice',
        values: { currency },
        alignRight: true
      },
      { property: 'guests', translationKey: 'sales.dialog.table.guests', alignRight: true },
      {
        property: 'productionCost',
        translationKey: 'sales.dialog.table.productionCost',
        values: { currency },
        alignRight: true
      },
      {
        property: 'productionWeight',
        translationKey: 'sales.dialog.table.productionWeight',
        values: { massUnit },
        alignRight: true
      },
      { property: 'portions', translationKey: 'sales.dialog.table.portions', alignRight: true },
      {
        property: 'foodwasteWeight',
        translationKey: 'sales.dialog.table.weight',
        values: { massUnit },
        alignRight: true
      }
    ];

    const tableRows = [];

    if (sortedSales) {
      for (let i = 0; i < sortedSales.length; i++) {
        const sale = sortedSales[i];
        const registrations = registrationsByDate[sale.date];
        const wasteWeight = registrations ? sum(registrations.map((r) => r.amount)) : 0;
        tableRows.push(
          <TableRow
            key={`${sale.date}-${sale.accountId}`}
            hover
            onClick={() => historyItemOnClick(sale)}
            selected={
              selectedSale &&
              selectedSale.date === sale.date &&
              selectedSale.accountId == sale.accountId
            }
          >
            <TableCell>{moment(sale.date).format('L')}</TableCell>
            <TableCell align='right'>
              <FormattedCost value={sale.income} style='decimal' />
            </TableCell>
            <TableCell align='right'>
              <FormattedCost value={sale.portionPrice} style='decimal' />
            </TableCell>
            <TableCell align='right'>{sale.guests}</TableCell>
            <TableCell align='right'>
              <FormattedCost value={sale.productionCost} style='decimal' />
            </TableCell>
            <TableCell align='right'>
              <FormattedMass value={sale.productionWeight} normalized={true} style='decimal' />
            </TableCell>
            <TableCell align='right'>{sale.portions}</TableCell>
            <TableCell align='right'>
              <FormattedMass value={wasteWeight} normalized={true} style='decimal' />
            </TableCell>
          </TableRow>
        );
      }
    }
    return (
      <Table stickyHeader style={{ overflow: 'auto', whiteSpace: 'nowrap' }}>
        <TableHead>
          <TableRow>
            {headerData.map(({ property, translationKey, values, alignRight }) => (
              <HeaderCell
                key={property}
                onClick={() => this.changeSorting(property as keyof Sale)}
                active={orderBy === property}
                direction={order}
                alignRight={alignRight}
                tooltip={<FormattedMessage id='data_table.sort' />}
              >
                {translationKey && <FormattedMessage id={translationKey} values={values} />}
              </HeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <CSSTransitionGroup
          transitionName='slide'
          transitionAppear
          transitionAppearTimeout={250}
          transitionEnterTimeout={250}
          transitionLeaveTimeout={250}
          component={TableBody}
          key={`tbody`}
        >
          {tableRows}
        </CSSTransitionGroup>
      </Table>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  sales: state.sales.yearlySales,
  // to do selector registration by date
  registrationsByDate: getYearlyRegistrationsByDate(state),
  enableGuestRegistrationFlow: state.settings.enableGuestRegistrationFlow,
  currency: state.settings.currency
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<unknown, unknown, RegistrationsActions | SalesActions>
) => ({
  getYearlySalesAndRegistrations: async () => {
    await Promise.all([
      dispatch(registrationsDispatch.getYearlyRegistrations()),
      dispatch(salesDispatch.getYearlySales())
    ]);
  }
});

export default connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(SalesHistory));
